import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Toast } from 'antd-mobile'
import {
  getRocketChatRooms,
  rocketChatLogin,
  getRocketMessagesReceipt,
  getMe
} from '../../api'
import ChatHeader from './ChatHeader'
import ChannelList from './ChannelList'
import IosSpinner from './IosSpinner'
import { loadUser, setTokenItem, saveUser, getToken, getCachedRoomsData, setCachedRoomsData, clearRoomsCache } from '../../utils'
import './styles/Chat.css'

const Chat = ({ getPerson }) => {
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [readReceipts, setReadReceipts] = useState([])
  const [userAvatars, setUserAvatars] = useState([])

  const rocketChatRef = useRef(null)
  const wsRef = useRef(null)
  const initRef = useRef(null)
  const subscribedRooms = useRef(new Set())
  const wrapperRef = useRef(null)
  const readReceiptsRef = useRef([])

  const [user, setUser] = useState(() => loadUser())

  const reLogin = async (personId) => {
    const res = await rocketChatLogin(personId)
    rocketChatRef.current = res
    localStorage.setItem('rocket-chat-data', JSON.stringify(res))
  }

  const refreshPerson = async (token) => {
    setTokenItem(token)
    const { person } = await getMe()
    setUser(person)
    saveUser(person)

    // Clear rooms cache when user/token changes
    clearRoomsCache()
  }

  useEffect(() => {
    window.handleMessageFromNative = (token) => {
      refreshPerson(token)
    }

    if (navigator.userAgent.includes('life.apeiron.ios')) {
      if (!getToken() || !loadUser()) {
        try {
          window.webkit?.messageHandlers?.loadUser?.postMessage({})
        } catch (err) {
          console.error('🚨 Failed to request token from Swift:', err)
        }
      }
    }
  }, [])

  const init = async () => {
    if (!user?.id) return
    setLoading(true)

    try {
      const personId = user?.id
      let rocketChatData = JSON.parse(localStorage.getItem('rocket-chat-data'))

      if (!rocketChatData && personId) {
        rocketChatData = await rocketChatLogin(personId)
        localStorage.setItem('rocket-chat-data', JSON.stringify(rocketChatData))
      }

      rocketChatRef.current = rocketChatData
      webSocketConnect()
    } catch (err) {
      console.error(err)
      setLoading(false)
      Toast.show({ content: 'Failed to load channels', icon: 'fail', duration: 3000 })
    }
  }

  const webSocketConnect = useCallback(() => {
    const wsStart = performance.now()
    const person = user
    const rocketChatData = JSON.parse(localStorage.getItem('rocket-chat-data'))
    if (!rocketChatData || !person) return

    rocketChatRef.current = rocketChatData
    const ws = new WebSocket(process.env.REACT_APP_ROCKET_CHAT_WS_URL)
    wsRef.current = ws

    const afterLoginSuccess = async () => {
      let client = null
      if (getPerson) client = await getPerson()

      // Check for cached rooms data first
      let res = getCachedRoomsData()

      // Fetch fresh data if no valid cache
      if (!res) {
        res = await getRocketChatRooms({
          person_id: person.id,
          client_id: person.id
        })

        // Cache the rooms data
        setCachedRoomsData(res)
      }

      const privateChannels = res?.rooms
        .filter(room => room.t === 'p' && room._id)
        .map(room => ({ ...room, threadUnreadCount: 0 }))

      const dmsList = res?.rooms
        .filter(room => room.t === 'd' && room._id)
        .map(dm => ({
          ...dm,
          name: dm.usernames?.find(
            username => username.split('_')[1] !== String(person.id)
          ) || '_',
          threadUnreadCount: 0
        }))

      const allChannels = [...privateChannels, ...dmsList]
      setChannels(allChannels)

      if (res?.userAvatars) {
        setUserAvatars(res.userAvatars)
      } else if (res?.rooms) {
        const allUsernames = new Set()
        res.rooms.forEach(room => {
          if (room.usernames && Array.isArray(room.usernames)) {
            room.usernames.forEach(username => allUsernames.add(username))
          }
        })

        const avatars = Array.from(allUsernames).map(username => ({
          username,
          avatar: null
        }))
        setUserAvatars(avatars)
      }

      const channelIds = privateChannels.map(c => c._id)
      const dmIds = dmsList.map(dm => dm._id)

      let receipts = []
      try {
        receipts = await getRocketMessagesReceipt({
          personId: person.id,
          channels: [...channelIds, ...dmIds]
        })
      } catch (error) {
        console.warn('Failed to fetch read receipts:', error)
      }

      const channelsWithThreadUnread = allChannels.map(channel => {
        const receipt = receipts.find(r => r.rid === channel._id)
        return {
          ...channel,
          threadUnreadCount: Array.isArray(receipt?.tunread) ? receipt.tunread.length : 0
        }
      })

      setChannels(channelsWithThreadUnread)
      setReadReceipts(receipts)
      readReceiptsRef.current = receipts;

      [...channelIds, ...dmIds].forEach(id => {
        if (!id || subscribedRooms.current.has(id)) return
        subscribedRooms.current.add(id)
        ws.send(JSON.stringify({
          msg: 'sub',
          id: `room-${id}`,
          name: 'stream-room-messages',
          params: [id, false]
        }))
      })

      setLoading(false)
      Toast.clear()
    }

    ws.onopen = () => {
      ws.send(JSON.stringify({
        msg: 'connect',
        version: '1',
        support: ['1']
      }))
    }

    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data)
      if (!rocketChatRef.current) return

      if (data.msg === 'connected') {
        ws.send(JSON.stringify({
          msg: 'method',
          method: 'login',
          id: '1',
          params: [{ resume: rocketChatRef.current.authToken }]
        }))
      }

      if (data.msg === 'ping') {
        ws.send(JSON.stringify({ msg: 'pong' }))
      }

      if (data.msg === 'result' && data.id === '1') {
        if (data.error) {
          console.warn('Login failed, retrying...', data.error)
          await reLogin(person.id)
          ws.send(JSON.stringify({
            msg: 'method',
            method: 'login',
            id: '1',
            params: [{ resume: rocketChatRef.current.authToken }]
          }))
        } else {
          await afterLoginSuccess()
        }
      }

      if (data.msg === 'changed' && data.collection === 'stream-room-messages') {
        const msg = data.fields?.args?.[0]
        if (!msg?.rid) return
        if (!user?.id) return

        if (msg.u?._id === user.id || msg.u?._id === rocketChatRef.current?.userId) {
          return
        }

        let receipts = []
        try {
          receipts = await getRocketMessagesReceipt({
            personId: user.id,
            channels: [msg.rid]
          })
        } catch (error) {
          console.warn('Failed to fetch read receipts for real-time update:', error)
        }

        setReadReceipts(prev => {
          const updated = prev.map(r => r.rid === msg.rid ? receipts[0] : r)
          readReceiptsRef.current = updated
          return updated
        })

        setChannels(prevChannels =>
          prevChannels.map(channel => {
            const receipt = receipts.find(r => r.rid === channel._id)
            return {
              ...channel,
              threadUnreadCount: Array.isArray(receipt?.tunread) ? receipt.tunread.length : 0
            }
          })
        )
      }
    }
  }, [getPerson, user])

  useEffect(() => {
    return () => {
      wsRef.current?.close()
    }
  }, [])

  useEffect(() => {
    initRef.current = init
  }, [init])

  useEffect(() => {
    if (user?.id) {
      init()
    }
  }, [user, webSocketConnect])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && wsRef.current?.readyState !== WebSocket.OPEN) {
        webSocketConnect()
      }
    }
    const handleOnline = () => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        webSocketConnect()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [webSocketConnect])

  const handleChannelSelect = (channelId) => {
    setSelectedChannel(channelId)
    const ws = new WebSocket(process.env.REACT_APP_ROCKET_CHAT_WS_URL)
    ws.onopen = () => {
      ws.send(JSON.stringify({
        msg: 'method',
        method: 'readMessages',
        id: 'read',
        params: [channelId]
      }))
    }
    const updatedReceipts = readReceiptsRef.current.map(receipt =>
      receipt.rid === channelId ? { ...receipt, unread: 0 } : receipt
    )
    setReadReceipts(updatedReceipts)
    readReceiptsRef.current = updatedReceipts
  }

  const handleBack = () => {
    if (window.webkit?.messageHandlers?.goBack) {
      window.webkit.messageHandlers.goBack.postMessage(null)
    } else {
      window.history.back()
    }
  }

  return (
    <div className="chat-wrapper" ref={wrapperRef}>
      <div className="chat-container">
        <ChatHeader title="Chat" onBack={handleBack} />
        {loading && <IosSpinner />}
        {!loading && (
          <ChannelList
            channels={channels}
            readReceipts={readReceipts}
            onSelectChannel={handleChannelSelect}
            isDm={false}
            userAvatars={userAvatars}
          />
        )}
      </div>
    </div>
  )
}

export default Chat
